import React, { useState, useEffect } from 'react';
import { Modal, Stack, Text, Button, Group, TextInput, ColorPicker, ActionIcon, Alert, Select } from '@mantine/core';
import { Settings, Plus, GripVertical, X, Palette, RotateCcw, AlertCircle } from 'lucide-react';
import { DndContext, DragEndEvent, MouseSensor, TouchSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { kanbanApi } from '../../api/kanbanApi';
import { useKanbanConfig, useKanbanTemplates } from '../../hooks/useKanbanConfig';
import { useUser } from '../../contexts/UserContext';
import type { KanbanColumn, KanbanConfig } from '../../types/kanban';
import { notifications } from '@mantine/notifications';
import './KanbanSettings.css';

interface KanbanSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  listId: number;
  listName: string;
}

// Sortable Column Item Component
const SortableColumnItem: React.FC<{
  column: KanbanColumn;
  onUpdate: (column: KanbanColumn) => void;
  onDelete: () => void;
  canDelete: boolean;
}> = ({ column, onUpdate, onDelete, canDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(column.name);
  const [color, setColor] = useState(column.color || '#6B7280');
  const [type, setType] = useState<'start' | 'progress' | 'done'>(column.type || 'progress');

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const handleSave = () => {
    onUpdate({ ...column, name: name.trim(), color, type });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setName(column.name);
    setColor(column.color || '#6B7280');
    setType(column.type || 'progress');
    setIsEditing(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="kanban-settings-column-item"
    >
      <div className="kanban-settings-column-header">
        <div {...attributes} {...listeners} className="kanban-settings-drag-handle">
          <GripVertical size={16} />
        </div>
        
        {isEditing ? (
          <TextInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Column name"
            style={{ flex: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') handleCancel();
            }}
            autoFocus
          />
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: column.color || '#6B7280',
              }}
            />
            <Text fw={500}>{column.name}</Text>
            {column.type === 'start' && (
              <Text size="xs" c="green" fw={600}>START</Text>
            )}
            {column.type === 'done' && (
              <Text size="xs" c="blue" fw={600}>DONE</Text>
            )}
          </div>
        )}

        <Group gap="xs">
          {isEditing ? (
            <>
              <Button size="xs" variant="light" onClick={handleSave}>
                Save
              </Button>
              <Button size="xs" variant="light" color="gray" onClick={handleCancel}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <ActionIcon
                variant="light"
                size="sm"
                onClick={() => setIsEditing(true)}
                title="Edit column"
              >
                <Palette size={14} />
              </ActionIcon>
              {canDelete && (
                <ActionIcon
                  variant="light"
                  size="sm"
                  color="red"
                  onClick={onDelete}
                  title="Delete column"
                >
                  <X size={14} />
                </ActionIcon>
              )}
            </>
          )}
        </Group>
      </div>

      {isEditing && (
        <div style={{ marginTop: '8px', padding: '8px 12px', background: 'var(--app-bg)', borderRadius: '6px' }}>
          <Stack gap="xs">
            <div>
              <Text size="xs" mb="xs" fw={500}>Column Type:</Text>
              <Select
                value={type}
                onChange={(value: string | null) => setType((value as 'start' | 'progress' | 'done') || 'progress')}
                data={[
                  { value: 'start', label: 'Start - Default for new todos' },
                  { value: 'progress', label: 'Progress - Work in progress' },
                  { value: 'done', label: 'Done - Marks todos as complete' }
                ]}
                size="xs"
              />
            </div>
            <div>
              <Text size="xs" mb="xs" fw={500}>Color:</Text>
              <ColorPicker
                format="hex"
                value={color}
                onChange={setColor}
                swatches={[
                  '#6B7280', '#3B82F6', '#10B981', '#F59E0B', 
                  '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6',
                  '#F97316', '#84CC16', '#6366F1', '#06B6D4'
                ]}
                size="sm"
              />
            </div>
          </Stack>
        </div>
      )}
    </div>
  );
};

export const KanbanSettings: React.FC<KanbanSettingsProps> = ({ isOpen, onClose, listId, listName }) => {
  const { currentUser } = useUser();
  const queryClient = useQueryClient();
  const { data: currentConfig } = useKanbanConfig(listId);
  const { data: templates = [] } = useKanbanTemplates();

  const [columns, setColumns] = useState<KanbanColumn[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize columns when config loads
  useEffect(() => {
    if (currentConfig?.columns) {
      setColumns([...currentConfig.columns].sort((a, b) => a.order - b.order));
      setHasChanges(false);
    }
  }, [currentConfig]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  // Mutations
  const updateConfigMutation = useMutation({
    mutationFn: (config: KanbanConfig) => kanbanApi.updateKanbanConfig(listId, config, currentUser?.email || ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-config', listId] });
      notifications.show({
        title: 'Success',
        message: 'Kanban configuration updated successfully',
        color: 'green',
      });
      setHasChanges(false);
    },
    onError: (error) => {
      notifications.show({
        title: 'Error',
        message: 'Failed to update Kanban configuration',
        color: 'red',
      });
      console.error('Failed to update kanban config:', error);
    },
  });

  const resetConfigMutation = useMutation({
    mutationFn: (templateName: string) => kanbanApi.resetKanbanConfig(listId, templateName, currentUser?.email || ''),
    onSuccess: (newConfig) => {
      queryClient.invalidateQueries({ queryKey: ['kanban-config', listId] });
      setColumns([...newConfig.columns].sort((a, b) => a.order - b.order));
      notifications.show({
        title: 'Success',
        message: 'Kanban configuration reset successfully',
        color: 'green',
      });
      setHasChanges(false);
    },
    onError: (error) => {
      notifications.show({
        title: 'Error',
        message: 'Failed to reset Kanban configuration',
        color: 'red',
      });
      console.error('Failed to reset kanban config:', error);
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = columns.findIndex(col => col.id === active.id);
    const newIndex = columns.findIndex(col => col.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newColumns = [...columns];
    const [movedColumn] = newColumns.splice(oldIndex, 1);
    newColumns.splice(newIndex, 0, movedColumn);

    // Update order values
    const updatedColumns = newColumns.map((col, index) => ({ ...col, order: index }));
    setColumns(updatedColumns);
    setHasChanges(true);
  };

  const addColumn = () => {
    const newColumn: KanbanColumn = {
      id: `column-${Date.now()}`,
      name: 'New Column',
      color: '#6B7280',
      order: columns.length,
    };
    setColumns([...columns, newColumn]);
    setHasChanges(true);
  };

  const updateColumn = (id: string, updates: Partial<KanbanColumn>) => {
    setColumns(cols => cols.map(col => col.id === id ? { ...col, ...updates } : col));
    setHasChanges(true);
  };

  const deleteColumn = (id: string) => {
    if (columns.length <= 1) {
      notifications.show({
        title: 'Cannot Delete',
        message: 'You must have at least one column',
        color: 'orange',
      });
      return;
    }

    setColumns(cols => cols.filter(col => col.id !== id));
    setHasChanges(true);
  };

  const handleSave = () => {
    const config: KanbanConfig = { columns };
    updateConfigMutation.mutate(config);
  };

  const handleReset = (templateName: string) => {
    resetConfigMutation.mutate(templateName);
  };

  const handleClose = () => {
    if (hasChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmed) return;
    }
    onClose();
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={
        <Group gap="sm">
          <Settings size={20} />
          <div>
            <Text fw={600}>Kanban Settings</Text>
            <Text size="sm" style={{ color: 'var(--app-text-muted)' }}>Configure columns for "{listName}"</Text>
          </div>
        </Group>
      }
      size="lg"
      styles={{
        content: {
          backgroundColor: 'var(--app-surface)',
          border: '1px solid var(--app-border)'
        },
        header: {
          backgroundColor: 'var(--app-surface)',
          borderBottom: '1px solid var(--app-border)',
          color: 'var(--app-text-primary)',
          padding: '20px 24px 16px 24px'
        },
        title: {
          color: 'var(--app-text-primary)',
          fontWeight: 600
        },
        close: {
          color: 'var(--app-text-secondary)',
          '&:hover': {
            backgroundColor: 'var(--app-hover)'
          }
        },
        body: { padding: '0 24px 24px 24px' }
      }}
    >
      <Stack gap="lg">
        {/* Template Selection */}
        {templates.length > 0 && (
          <div>
            <Text size="sm" fw={500} mb="xs">Quick Templates</Text>
            <Group gap="xs">
              {templates.map((template) => (
                <Button
                  key={template.name}
                  size="xs"
                  variant="light"
                  leftSection={<RotateCcw size={12} />}
                  onClick={() => handleReset(template.name)}
                  loading={resetConfigMutation.isPending}
                >
                  {template.name}
                </Button>
              ))}
            </Group>
            <Text size="xs" style={{ color: 'var(--app-text-muted)' }} mt="xs">
              Reset your columns to use a predefined template
            </Text>
          </div>
        )}

        {/* Columns Configuration */}
        <div>
          <Group justify="space-between" align="center" mb="sm">
            <Text size="sm" fw={500}>Columns</Text>
            <Button
              size="xs"
              leftSection={<Plus size={12} />}
              onClick={addColumn}
              variant="light"
            >
              Add Column
            </Button>
          </Group>

          <Text size="xs" style={{ color: 'var(--app-text-muted)' }} mb="md">
            Drag and drop to reorder columns. Click the palette icon to edit a column.
          </Text>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={columns.map(c => c.id)} strategy={verticalListSortingStrategy}>
              <Stack gap="xs">
                {columns.map((column) => (
                  <SortableColumnItem
                    key={column.id}
                    column={column}
                    onUpdate={(updatedColumn) => updateColumn(column.id, updatedColumn)}
                    onDelete={() => deleteColumn(column.id)}
                    canDelete={columns.length > 1}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>

          {columns.length === 0 && (
            <Alert icon={<AlertCircle size={16} />} color="orange" variant="light">
              You need at least one column for your Kanban board.
            </Alert>
          )}
        </div>

        {/* Actions */}
        <Group justify="flex-end" gap="sm" pt="md" style={{ borderTop: '1px solid var(--app-border)' }}>
          <Button variant="light" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={updateConfigMutation.isPending}
            disabled={!hasChanges || columns.length === 0}
          >
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
